import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { TypedDateInput } from "@/components/TypedDateInput";
import { useUnsavedChanges } from "@/navigation/unsavedChanges";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;

const createDraft = (job, project) => ({
  scheduled_date: job?.scheduled_date || project.scheduled_date || "",
  foreman: job?.foreman || "",
  crew_members: (job?.crew_members || []).join(", "),
  assignment_notes: job?.assignment_notes || "",
  equipment_ids: job?.equipment_ids || [],
  assigned_user_ids: job?.assigned_user_ids || [],
  status: job?.status || "scheduled",
});

export const ProjectActiveJobDrawer = ({
  canHumanAuthority,
  job,
  onClose,
  onSaved,
  project,
}) => {
  const [draft, setDraft] = useState(() => createDraft(job, project));
  const baseline = useRef(draft);
  const sourceKey = useRef("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [resources, setResources] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [resourceSearch, setResourceSearch] = useState({});
  useEffect(() => {
    let active = true;
    axios.get(`${API}/assignment-resources`).then(({ data }) => { if (active) setResources(data); })
      .catch(() => { if (active) setError("Crew and equipment choices could not load. Reopen this assignment to try again."); });
    if (job?.id) axios.get(`${API}/assignment-conflicts`, { params: { job_id: job.id } })
      .then(({ data }) => { if (active) setWarnings(data.warnings || []); }).catch(() => {});
    return () => { active = false; };
  }, [job?.id]);

  useEffect(() => {
    const key = `${project.id}:${job?.id || "new"}`;
    if (sourceKey.current === key) return;
    sourceKey.current = key;
    baseline.current = createDraft(job, project);
    setDraft(baseline.current);
    setError("");
  }, [job, project]);

  const update = (field, value) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    const payload = {
      scheduled_date: draft.scheduled_date,
      foreman: draft.foreman.trim(),
      crew_members: draft.crew_members
        .split(",")
        .map((member) => member.trim())
        .filter(Boolean),
      assignment_notes: draft.assignment_notes.trim(),
      status: draft.status,
      equipment_ids: draft.equipment_ids,
      assigned_user_ids: draft.assigned_user_ids,
    };

    try {
      let response;
      if (job?.id) {
        response = await axios.patch(`${API}/active-jobs/${job.id}`, payload);
      } else {
        response = await axios.post(`${API}/active-jobs`, {
          ...payload,
          project_id: project.id,
          active: true,
        });
      }
      toast.success(job?.id ? "Field assignment saved." : "Field assignment added.");
      await onSaved();
      if (response.data.assignment_warnings?.length) {
        setWarnings(response.data.assignment_warnings);
        toast.warning("Saved, but crew or equipment is also assigned elsewhere on this date.");
      }
      onClose();
      return true;
    } catch (requestError) {
      setError(
        typeof requestError.response?.data?.detail === "string" ? requestError.response.data.detail : "Field assignment could not be saved. Check the selected date and resources.",
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const changeActivity = async (action) => {
    if (!job?.id || !window.confirm(`Confirm: ${action} this field assignment?`)) return;
    try {
      await axios.post(`${API}/active-jobs/${job.id}/${action}`);
      toast.success(`Field assignment ${action}d.`);
      await onSaved();
      onClose();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || "Field assignment could not be updated.");
    }
  };

  const guard = useUnsavedChanges({ dirty: canHumanAuthority && JSON.stringify(draft) !== JSON.stringify(baseline.current),
    label: "Field assignment", busy: saving, onSave: () => save({ preventDefault() {} }),
    onDiscard: () => setDraft(baseline.current) });
  return (
    <Sheet open onOpenChange={open => { if (!open) guard.requestClose(onClose); }}>
      <SheetContent className="project-record-drawer" data-testid="project-active-job-drawer">
        <SheetHeader>
          <SheetTitle>{job?.id ? "Edit field assignment" : "New field assignment"}</SheetTitle>
          <SheetDescription data-testid="project-active-job-fixed-project">
            {project.project_code} · {project.name}
          </SheetDescription>
        </SheetHeader>
        <form className="project-record-drawer-form" onSubmit={save}>
          {error && (
            <p
              className="project-record-drawer-error"
              data-testid="project-active-job-error"
            >
              {error}
            </p>
          )}
          <label htmlFor="project-active-job-scheduled-date">
            Scheduled date
            <TypedDateInput
              data-testid="project-active-job-scheduled-date"
              id="project-active-job-scheduled-date"
              onChange={(value) => update("scheduled_date", value)}
              value={draft.scheduled_date}
            />
          </label>
          <label htmlFor="project-active-job-status">
            Assignment status
            <select
              data-testid="project-active-job-status"
              id="project-active-job-status"
              onChange={(event) => update("status", event.target.value)}
              value={draft.status}
            >
              <option value="scheduled">Scheduled</option>
              <option value="in_progress">In Progress</option>
              <option value="complete">Complete</option>
              <option value="cancelled">Canceled</option>
            </select>
          </label>
          <label htmlFor="project-active-job-foreman">
            Foreman
            <input
              data-testid="project-active-job-foreman"
              id="project-active-job-foreman"
              onChange={(event) => update("foreman", event.target.value)}
              value={draft.foreman}
            />
          </label>
          <label htmlFor="project-active-job-crew-members">
            Crew members
            <input
              data-testid="project-active-job-crew-members"
              id="project-active-job-crew-members"
              onChange={(event) => update("crew_members", event.target.value)}
              placeholder="Separate names with commas"
              value={draft.crew_members}
            />
          </label>
          {resources && [
            ["assigned_user_ids", "Crew accounts", resources.users],
            ["equipment_ids", "Equipment", resources.equipment],
          ].map(([field, label, choices]) => <fieldset key={field} disabled={saving}>
            <legend>{label}</legend>
            <input aria-label={`Search ${label.toLowerCase()}`} placeholder={`Search ${label.toLowerCase()}`} value={resourceSearch[field] || ""}
              onChange={event => setResourceSearch(current => ({ ...current, [field]: event.target.value }))} />
            {choices.length === 0 && <p>No {label.toLowerCase()} available.</p>}
            <div style={{ maxHeight: 180, overflowY: "auto" }}>
            {choices.filter(item => item.name.toLowerCase().includes((resourceSearch[field] || "").toLowerCase())).map(item => <label key={item.id} style={{ display: "flex", gap: 8 }}>
              <input type="checkbox" checked={draft[field].includes(item.id)} onChange={event =>
                update(field, event.target.checked ? [...draft[field], item.id] : draft[field].filter(id => id !== item.id))} />
              {item.name}
            </label>)}
            </div>
          </fieldset>)}
          {warnings.length > 0 && <div role="status">
            <p>Schedule conflicts — assignments are saved. Review these jobs:</p>
            {warnings.map(item => <p key={item.job_id}><Link to={`/active-jobs/${item.job_id}`}>{item.project_code} · {item.project_name}</Link>: {item.message}</p>)}
          </div>}
          <label htmlFor="project-active-job-notes">
            Assignment notes
            <textarea
              data-testid="project-active-job-notes"
              id="project-active-job-notes"
              onChange={(event) => update("assignment_notes", event.target.value)}
              value={draft.assignment_notes}
            />
          </label>
          <SheetFooter>
            {canHumanAuthority && job?.id && (
              <button
                data-testid={`project-active-job-${job.active ? "deactivate" : "restore"}-${job.id}`}
                onClick={() => guard.requestClose(() => changeActivity(job.active ? "deactivate" : "restore"))}
                type="button"
              >
                {job.active ? "Deactivate" : "Restore"}
              </button>
            )}
            <button
              className="board-refresh-button"
              data-testid="project-active-job-cancel-button"
              disabled={saving}
              onClick={() => guard.requestClose(onClose)}
              type="button"
            >
              Cancel
            </button>
            <button
              className="invoice-primary-button"
              data-testid="project-active-job-save-button"
              disabled={saving || !resources}
              type="submit"
            >
              {saving ? "Saving…" : job?.id ? "Save field assignment" : "Add field assignment"}
            </button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
};
